import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  from,
  NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { getCsrfTokenHeader } from '@veeva-ctv/utils';
import { ping } from '@/services/auth';
import * as Sentry from '@sentry/nextjs';
import { setContext } from '@apollo/client/link/context';

// Node.js environment uses API_ENDPOINT, browser uses NEXT_PUBLIC_API_ENDPOINT
const apolloURI =
  typeof window === 'undefined'
    ? process.env.NEXT_PUBLIC_SSR_API_ENDPOINT
    : process.env.NEXT_PUBLIC_API_ENDPOINT;
const uploadLink = createUploadLink({
  uri: apolloURI,
});

/**
 * 403/401 (forbidden / unauthorized / unauthenticated) responses could come in one of the
 * following formats covered by statusCodeLink and logoutLink, forwarding to login
 *
 * also check for CSRF token related errors and handle similarly
 */

const statusCodeLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((data) => {
    if (data.errors && data.errors.length) {
      const ctvErrorCode = data.errors[0].extensions?.ctvErrorCode as number;
      if (typeof window !== 'undefined') {
        if (ctvErrorCode) {
          Sentry.captureMessage('ctvErrorCode caught by statusCodeLink', {
            level: 'error',
            tags: {
              ctvErrorCode: ctvErrorCode,
            },
            extra: {
              operationName: operation.operationName,
            },
          });
        }
        if (ctvErrorCode === 401) {
          //Do nothing? there's no authentication so we can not log the user out
        } else if (ctvErrorCode === 403) {
          window.location.assign(`${process.env.NEXT_PUBLIC_BASE_PATH as string}/403`);
        } else if (ctvErrorCode === 404) {
          window.location.assign(`${process.env.NEXT_PUBLIC_BASE_PATH as string}/404`);
        } else if (ctvErrorCode === 500) {
          window.location.assign(`${process.env.NEXT_PUBLIC_BASE_PATH as string}/500`);
        }
      }
    }
    return data;
  });
});

/**
 * CSRF logic lives here, every mutation will FIRST call /auth/ping to get a fresh CSRF token
 * which will then be set as a header in the request context
 *
 * we no longer store CSRF tokens in localStorage CTV-3938
 */
const pingLink = setContext(async (request, { headers }) => {
  const isMutation = request.query.definitions
    // TS Hack should be safe since n actually _does_ have an operation property and even
    // if it didn't it would fall back to the empty string
    .map((n) => (n as { operation: string }).operation || '')
    // Mutations only
    .includes('mutation');
  return new Promise((success, fail) => {
    if (isMutation) {
      void ping().then(
        ({ headers: pingHeaders }) => {
          const csrfTokenHeader = getCsrfTokenHeader(pingHeaders);
          success({
            headers: {
              ...headers,
              ...csrfTokenHeader,
            },
          });
        },
        () => {
          Sentry.captureMessage('pingLink error', {
            level: 'error',
            extra: {
              operationName: request.operationName,
            },
          });
          fail();
        }
      );
    } else {
      success({});
    }
  });
});

const logoutLink = onError(({ operation, networkError }) => {
  if (
    networkError &&
    'statusCode' in networkError &&
    // We'll use 401s for all "Unauthorized" since S3 treats 404s as 403s
    (networkError.statusCode === 403 || networkError.statusCode === 401)
  ) {
    Sentry.captureMessage('networkError.statusCode caught by logoutLink', {
      level: 'error',
      tags: {
        'networkError.statusCode': networkError.statusCode,
      },
      extra: {
        operationName: operation.operationName,
      },
    });
  }
});

const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  connectToDevTools:
    process.env.NEXT_PUBLIC_APOLLO_CONNECT_TO_DEVTOOLS === 'true' ? true : undefined,
  credentials: 'same-origin',
  link: from([
    statusCodeLink,
    logoutLink,
    pingLink,
    // This TS error made no sense, fix is reassigning the exact same type
    // `Type 'ApolloLink' is not assignable to type 'ApolloLink | RequestHandler'`
    uploadLink as unknown as ApolloLink,
  ]),
  cache: new InMemoryCache(),
});

export default client;
